<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestFieldGrid from '@/components/finest-field-grid/FinestFieldGrid.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableValue,
} from '@/components/finest-field-grid/types'
import PropertyPhotoGallery from '@/components/portfolio/PropertyPhotoGallery.vue'
import { useEnumOptions } from '@/composables/useEnumOptions'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { useEstateObjectUpdate } from '@/pages/investors/portfolio/useEstateObjectUpdate'
import type { ExtendedEnum, PersonGroup } from '@/types'
import type { FieldStateMap } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import EstateObjectType = App.Enums.EstateObject.EstateObjectType

const props = defineProps<{
  fieldStates: FieldStateMap
  estateObject: EstateObjectData
  estateObjectTypes: ExtendedEnum<EstateObjectType>[]
  landRegisterChargeDefaults: Record<string, { label: string; rate: number }>
  personOptions: PersonGroup[]
  photos: AttachmentData[]
}>()

const { t } = useI18n()
const toEnumOptions = useEnumOptions()

const ownerOptions = computed(() =>
  props.personOptions.flatMap((group) => group.items.map((item) => ({ value: item.id, label: item.name }))),
)

const stateOptions = computed(() =>
  Object.entries(props.landRegisterChargeDefaults).map(([value, { label }]) => ({ value, label })),
)

const masterDataFields = computed<InlineEditableField[]>(() => [
  {
    key: 'estate_object_type',
    label: t('fields.estate_object_type'),
    type: 'select',
    fullWidth: true,
    options: toEnumOptions(props.estateObjectTypes, 'enums.estateObjectType'),
  },
  { key: 'owner_id', label: t('fields.owner'), type: 'select', options: ownerOptions.value },
  {
    key: 'construction_year',
    label: t('fields.construction_year'),
    type: 'number',
    useGrouping: false,
  },
])

const locationFields = computed<InlineEditableField[]>(() => [
  { key: 'street', label: t('fields.street'), type: 'text', fullWidth: true },
  { key: 'house_number', label: t('fields.house_number'), type: 'text' },
  { key: 'address_addition', label: t('fields.address_addition'), type: 'text' },
  { key: 'zip_code', label: t('fields.zip_code'), type: 'text' },
  { key: 'city', label: t('fields.city'), type: 'text' },
  { key: 'state', label: t('fields.state'), type: 'select', fullWidth: true, options: stateOptions.value },
])

const featureFields = computed<InlineEditableField[]>(() => [
  { key: 'has_monument_protection', label: t('fields.has_monument_protection'), type: 'boolean' },
  { key: 'has_gambling', label: t('fields.has_gambling'), type: 'boolean' },
  { key: 'has_leasehold', label: t('fields.has_leasehold'), type: 'boolean' },
  { key: 'has_mountain_damage_waiver', label: t('fields.has_mountain_damage_waiver'), type: 'boolean' },
  { key: 'has_homeowners_association', label: t('fields.has_homeowners_association'), type: 'boolean' },
])

const editableKeys = computed(() =>
  [...masterDataFields.value, ...locationFields.value, ...featureFields.value].map((field) => field.key),
)

const seedModel = (): InlineEditableModel => {
  const source = props.estateObject as unknown as Record<string, InlineEditableValue>

  return Object.fromEntries(editableKeys.value.map((key) => [key, source[key] ?? null]))
}

const { model, confirm } = useSyncedModel(seedModel)

const update = useEstateObjectUpdate(props.estateObject.id ?? '', confirm)

const ownerTypeFor = (ownerId: InlineEditableValue): string | null =>
  props.personOptions.flatMap((group) => group.items).find((item) => item.id === ownerId)?.type ?? null

const save = ({ key, value }: InlineEditableCommitPayload) => {
  if (key === 'owner_id') {
    update({ owner_id: value, owner_type: ownerTypeFor(value) })

    return
  }

  update({ [key]: value })
}
</script>

<template>
  <div class="grid items-start gap-4 lg:grid-cols-2">
    <PropertyPhotoGallery :estate-object-id="estateObject.id ?? ''" :photos="photos" />

    <FinestCard :title="t('cards.location')" icon="pi pi-map-marker">
      <FinestFieldGrid v-model="model" :fields="locationFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>

    <FinestCard :title="t('cards.masterData')" icon="pi pi-home">
      <FinestFieldGrid v-model="model" :fields="masterDataFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>

    <FinestCard :title="t('cards.features')" icon="pi pi-shield">
      <FinestFieldGrid v-model="model" :fields="featureFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "cards": {
      "masterData": "Stammdaten",
      "location": "Lage",
      "features": "Merkmale"
    },
    "fields": {
      "estate_object_type": "Objektart",
      "owner": "Eigentümer",
      "construction_year": "Baujahr",
      "street": "Straße",
      "house_number": "Hausnummer",
      "address_addition": "Zusatz",
      "zip_code": "PLZ",
      "city": "Ort",
      "state": "Bundesland",
      "has_monument_protection": "Denkmalschutz",
      "has_gambling": "Glücksspiel",
      "has_leasehold": "Erbpacht",
      "has_mountain_damage_waiver": "Bergschadensverzicht",
      "has_homeowners_association": "Eigentümergemeinschaft"
    }
  },
  "en": {
    "cards": {
      "masterData": "Master data",
      "location": "Location",
      "features": "Characteristics"
    },
    "fields": {
      "estate_object_type": "Property type",
      "owner": "Owner",
      "construction_year": "Year of construction",
      "street": "Street",
      "house_number": "House number",
      "address_addition": "Addition",
      "zip_code": "Postal code",
      "city": "City",
      "state": "Federal state",
      "has_monument_protection": "Listed building",
      "has_gambling": "Gambling use",
      "has_leasehold": "Leasehold",
      "has_mountain_damage_waiver": "Mining damage waiver",
      "has_homeowners_association": "Homeowners association"
    }
  }
}
</i18n>
